/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { Portal } from '@radix-ui/react-portal'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import * as React from 'react'

import { ExternalLink } from '@/components/marketing/fragments/external-link'
import { ThemeSwitcher } from '@/components/marketing/fragments/theme-switcher'
import { MENU_LINKS } from '@/components/marketing/marketing-links'
import { Button, buttonVariants } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Routes } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react'
import Image from 'next/image'
import { Switcher } from './switcher-language'
import Logo from 'public/marketing/logos/group.png'

export function MobileMenu({
  className,
  ...other
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(false)
  const pathname = usePathname()

  React.useEffect(() => {
    const handleRouteChangeStart = () => {
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur()
      }

      setOpen(false)
    }

    handleRouteChangeStart()
  }, [pathname])

  const handleChange = () => {
    const mediaQueryList = window.matchMedia('(min-width: 1024px)')
    setOpen((open) => (open ? !mediaQueryList.matches : false))
  }

  React.useEffect(() => {
    handleChange()
    const mediaQueryList = window.matchMedia('(min-width: 1024px)')
    mediaQueryList.addEventListener('change', handleChange)
    return () => mediaQueryList.removeEventListener('change', handleChange)
  }, [])

  const handleToggleMobileMenu = (): void => {
    setOpen((open) => !open)
  }

  return (
    <>
      <div className={cn('flex items-center justify-between', className)} {...other}>
        <Link href={Routes.Root}>
          <Image
            property='true'
            src={Logo}
            width={377}
            height={84}
            className='max-h-10 w-auto'
            alt='Logo Pione'
          />
        </Link>
        <Button
          variant='ghost'
          size='icon'
          aria-expanded={open}
          aria-label='Toggle Mobile Menu'
          onClick={handleToggleMobileMenu}
          className='flex aspect-square h-fit select-none flex-col items-center justify-center rounded-full'
        >
          <motion.div
            className='w-5 origin-center border-t-2 !border-muted-foreground'
            initial={{ translateY: '-3px' } as any}
            animate={
              open ?
                ({ rotate: '45deg', translateY: '1px' } as any)
              : { translateY: '-3px', rotate: '0deg' }
            }
            transition={{ bounce: 0, duration: 0.1 }}
          />
          <motion.div
            className='w-5 origin-center border-t-2 !border-muted-foreground'
            transition={{ bounce: 0, duration: 0.1 }}
            initial={{ translateY: '3px' } as any}
            animate={
              open ?
                ({ rotate: '-45deg', translateY: '-1px' } as any)
              : { translateY: '3px', rotate: '0deg', scaleX: 1 }
            }
          />
        </Button>
      </div>
      {open && (
        <Portal asChild>
          {/* <RemoveScroll allowPinchZoom enabled> */}
          <MainMobileMenu onLinkClicked={handleToggleMobileMenu} />
          {/* </RemoveScroll> */}
        </Portal>
      )}
    </>
  )
}

type MainMobileMenuProps = {
  onLinkClicked: () => void
}

function MainMobileMenu({ onLinkClicked }: MainMobileMenuProps): React.JSX.Element {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})
  const { i18n } = useLingui()

  return (
    <div className='fixed inset-0 z-50 mt-[65px] overflow-y-auto bg-background animate-in fade-in-0'>
      <div className='flex size-full flex-col items-start space-y-3 p-4'>
        <div className='flex w-full flex-col gap-2'>
          <Link
            href={Routes.Contact}
            className={cn(
              buttonVariants({
                variant: 'default',
                size: 'lg'
              }),
              'w-full rounded-xl'
            )}
            onClick={onLinkClicked}
          >
            Liên hệ
          </Link>
        </div>
        <ul className='w-full'>
          {MENU_LINKS.map((item) => (
            <li key={i18n._(item.title)} className='py-2'>
              {item.items ?
                <Collapsible
                  open={expanded[i18n._(item.title).toLowerCase()]}
                  onOpenChange={(isOpen) =>
                    setExpanded((prev) => ({
                      ...prev,
                      [i18n._(item.title).toLowerCase()]: isOpen
                    }))
                  }
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      className='flex h-9 w-full items-center justify-between px-4 text-left'
                    >
                      <span className='text-base font-medium'>{i18n._(item.title)}</span>
                      {expanded[i18n._(item.title).toLowerCase()] ?
                        <ChevronUp className='size-4' />
                      : <ChevronDown className='size-4' />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className='mt-2 pl-4'>
                      {item.items.map((subItem) => (
                        <li key={i18n._(subItem.title)}>
                          <Link
                            href={subItem.href || '#'}
                            target={subItem.external ? '_blank' : undefined}
                            rel={subItem.external ? 'noopener noreferrer' : undefined}
                            className={cn(
                              buttonVariants({ variant: 'ghost' }),
                              'm-0 h-auto w-full justify-start gap-4 p-1.5'
                            )}
                            onClick={onLinkClicked}
                          >
                            <div className='flex size-10 shrink-0 items-center justify-center rounded-xl border bg-background text-muted-foreground transition-colors group-hover:text-foreground'>
                              {subItem.icon}
                            </div>
                            <div>
                              <span className='text-sm font-medium'>
                                {i18n._(subItem.title)}
                                {subItem.external && (
                                  <ExternalLink className='-mt-2 ml-1 inline text-muted-foreground' />
                                )}
                              </span>
                              {subItem.description && (
                                <p className='text-xs text-muted-foreground max-w-52 overflow-ellipsis overflow-hidden'>
                                  {i18n._(subItem.description)}
                                </p>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              : <Link
                  href={item.href || '#'}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  className={cn(buttonVariants({ variant: 'ghost' }), 'w-full justify-start')}
                  onClick={onLinkClicked}
                >
                  <span className='text-base'>{i18n._(item.title)}</span>
                </Link>
              }
            </li>
          ))}
        </ul>
        <div className='flex w-full items-center justify-between gap-2 border-t border-border/40 p-4 pb-1'>
          <div className='text-base font-medium'>Theme</div>
          <ThemeSwitcher />
        </div>
        <div className='flex w-full items-center justify-between gap-2 border-y border-border/40 p-4 py-2'>
          <div className='text-base font-medium'>Language</div>
          <Switcher />
        </div>
      </div>
    </div>
  )
}
